import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { FiFileText, FiAlertCircle, FiCheckCircle, FiXCircle, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';

const ImportantPages = ({ siteId, dateFrom, dateTo }) => {
  const { currentLang, token, API_URL } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (siteId) {
      fetchPages();
    }
  }, [siteId]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/pages`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPages(data.pages || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки страниц');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const decodeBase64 = (str) => {
    if (!str) return '';
    const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(str) && str.length % 4 === 0;
    if (isBase64) {
      try {
        const decoded = atob(str);
        try {
          return decodeURIComponent(escape(decoded));
        } catch {
          return decoded;
        }
      } catch {
        return str;
      }
    } else {
      return str;
    }
  };

  const parseKeywords = (keyData) => {
    if (!keyData) return [];
    try {
      const decoded = decodeBase64(keyData);
      const keywords = [];
      const matches = decoded.match(/"([^\"]+)"/g);
      if (matches) {
        const extractedKeywords = matches.map(match => match.replace(/"/g, ''));
        keywords.push(...extractedKeywords.map(keyword => {
          try {
            return decodeURIComponent(escape(keyword));
          } catch {
            return keyword;
          }
        }));
      }
      if (keywords.length === 0) {
        const altMatches = decoded.match(/s:\d+:"([^\"]+)"/g);
        if (altMatches) {
          const altKeywords = altMatches.map(match => {
            const content = match.match(/s:\d+:"([^\"]+)"/);
            return content ? content[1] : '';
          }).filter(Boolean);
          keywords.push(...altKeywords.map(keyword => {
            try {
              return decodeURIComponent(escape(keyword));
            } catch {
              return keyword;
            }
          }));
        }
      }
      return keywords;
    } catch (error) {
      
      return [];
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200';
      case 2:
        return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200';
      case 3:
        return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-200';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return 'Отлично';
      case 2:
        return 'Хорошо';
      case 3:
        return 'Требует внимания';
      default:
        return 'Не определен';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 1:
        return <FiCheckCircle className="w-4 h-4" />;
      case 2:
        return <FiAlertCircle className="w-4 h-4" />;
      case 3:
        return <FiXCircle className="w-4 h-4" />;
      default:
        return <FiFileText className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    
    // Если дата в формате YYYY-MM-DD (строка)
    if (typeof dateString === 'string') {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('ru-RU');
      }
    }
    
    // Если дата в формате timestamp (число)
    if (typeof dateString === 'number') {
      const date = new Date(dateString * 1000);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('ru-RU');
      }
    }
    
    return 'Неверная дата';
  };

  if (loading) {
    return (
      <div className="min-h-[297mm] p-8 sm:p-12 bg-white dark:bg-navy-800">
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-gray-600 dark:text-gray-400">Загрузка важных страниц...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[297mm] p-8 sm:p-12 bg-white dark:bg-navy-800">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <FiXCircle className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Ошибка загрузки страниц
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[297mm] p-4 sm:p-6 bg-white dark:bg-navy-800">
      {/* Заголовок секции */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
          Важные страницы сайта
        </h2>
        <p className="text-gray-600 dark:text-gray-500 text-sm">
          Анализ ключевых страниц и их SEO показателей. Осуществляется ежедневный мониторинг всех важных страниц, включая контроль технических данных, SEO-метрик и поведенческих показателей. Оперативно выявляются и устраняются любые инциденты, корректируются отклонения от целевых значений. Страницы находятся под постоянным расширенным контролем с применением комплексной системы мониторинга.
        </p>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-xs w-full">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2 flex flex-row items-center w-full">
          <FiFileText className="w-4 h-4 text-blue-500 mr-2" />
          <span className="w-full">Всего: <b>{pages.length}</b></span>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded p-2 flex flex-row items-center w-full">
          <FiTrendingUp className="w-4 h-4 text-purple-500 mr-2" />
          <span className="w-full">Ключевых слов: <b>{pages.reduce((total, page) => total + parseKeywords(page.key).length, 0)}</b></span>
        </div>
      </div>

      {/* Таблица страниц */}
      <div>
        <table className="min-w-full text-xs border border-gray-200 dark:border-gray-700 rounded">
          <thead className="bg-gray-100 dark:bg-navy-700">
            <tr>
              <th className="p-2 font-semibold text-left">URL</th>
              <th className="p-2 font-semibold text-left">Title</th>
              <th className="p-2 font-semibold text-left">Description</th>
              <th className="p-2 font-semibold text-left">H1</th>
              <th className="p-2 font-semibold text-left">Ключевые слова</th>
              <th className="p-2 font-semibold text-left">Проверка</th>
            </tr>
          </thead>
          <tbody>
            {pages.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-400">
                  <FiFileText className="w-8 h-8 mx-auto mb-2" />
                  Нет важных страниц. Добавьте страницы для отслеживания в разделе SEO Страницы.
                </td>
              </tr>
            ) : (
              pages.map((page) => {
                const keywords = parseKeywords(page.key);
                return (
                  <tr key={page.id} className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-navy-800">
                    <td className="p-2 break-all max-w-[220px] text-blue-700 dark:text-blue-300 underline">
                      {page.url}
                    </td>
                    <td className="p-2 max-w-[180px]" title={page.title}>{page.title || '—'}</td>
                    <td className="p-2 max-w-[180px]" title={page.description}>{page.description || '—'}</td>
                    <td className="p-2 max-w-[120px]" title={page.h1}>{page.h1 || '—'}</td>
                    <td className="p-2 max-w-[180px]">
                      <div className="flex flex-col gap-1">
                        {keywords.length === 0 && <span className="text-gray-400">—</span>}
                        {keywords.slice(0, 3).map((keyword, idx) => (
                          <span key={idx} className="px-1 py-0.5 bg-blue-100 text-blue-800 rounded dark:bg-blue-900 dark:text-blue-200">
                            {keyword}
                          </span>
                        ))}
                        {keywords.length > 3 && (
                          <span className="px-1 py-0.5 bg-gray-100 text-gray-600 rounded dark:bg-gray-700 dark:text-gray-300">
                            +{keywords.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 whitespace-nowrap">{formatDate(dateTo)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportantPages; 